import { Field, Form, useFormikContext } from 'formik'
import * as React from 'react'
import {
  ChangeEvent,
  FC,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { IUpdateContactFormModel } from './typings'
import { styles } from './styling'
import {
  AddressFields,
  BaseTextField,
  MuiTextFieldFormik,
} from '@user/components/Formik'
import { IContactDTO } from '@user/store/contacts/typings'
import { ENTITY_TYPES } from '@sales-center/common'
import { MuiCheckboxFormik } from '@user/components/Formik/MuiCheckboxFormik'
import { MuiPhoneFieldFormik } from '@user/components/Formik/MuiPhoneFieldFormik'
import {
  EmailCollection,
  useEmailCollection,
} from '@user/components/EmailCollection/'
import { HeaderAddButton, SideDrawerForm } from '@sales-center/components'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import { useCheckIsMobileNumber } from '@user/store/utilities'
import { Theme } from '@mui/material/styles'

interface Props {
  contact?: IContactDTO
  entityType: ENTITY_TYPES | undefined
  entityId: number | undefined
  isPrimaryContact?: boolean
  onCancel: () => void
}

export const UpdateContactFormFields: FC<Props> = (props: Props) => {
  const { contact, onCancel, entityType, entityId, isPrimaryContact } = props

  const {
    submitForm,
    isSubmitting,
    setFieldValue,
    setValues,
    handleChange,
    setFieldTouched,
    values: {
      contactTime,
      company,
      emailAddresses,
      firstName,
      lastName,
      phoneNumber,
      altPhoneNumber,
    },
  } = useFormikContext<IUpdateContactFormModel>()

  useEffect(() => {
    setValues((current) => ({
      ...current,
      entityType: entityType,
      entityId: entityId,
    }))
  }, [entityId, entityType, setValues])

  useEffect(() => {
    setFieldValue('isPrimaryContact', !!isPrimaryContact)
  }, [isPrimaryContact, setFieldValue])

  //========== E V E N T S ==========
  const onSave = useCallback(() => {
    submitForm()
  }, [submitForm])

  const [requireEmail, requirePhone] = useMemo(
    () => [
      phoneNumber === '',
      !emailAddresses.find((x) => x.emailAddress !== ''),
    ],
    [emailAddresses, phoneNumber],
  )

  const [requireCompany, requireName] = React.useMemo(
    () => [firstName === '' && lastName === '', company === ''],
    [firstName, lastName, company],
  )

  const { addEmail, count: emailCount } =
    useEmailCollection<IUpdateContactFormModel>('emailAddresses')

  const addEmailDisabled = emailCount >= 3

  const { isMobileNumber: isMobilePhone, execute: checkIsPhoneMobile } =
    useCheckIsMobileNumber(phoneNumber)
  const { isMobileNumber: isMobileAltPhone, execute: checkIsAltPhoneMobile } =
    useCheckIsMobileNumber(altPhoneNumber)

  const onBlurPhone = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value, name } = event.target
      if (value == '') {
        setFieldValue(name == 'phoneNumber' ? 'phoneExt' : 'altPhoneExt', '')
      }
      setFieldValue(name, value)
      if (name == 'phoneNumber') {
        checkIsPhoneMobile(value)
      } else {
        checkIsAltPhoneMobile(value)
      }
    },
    [setFieldValue, checkIsAltPhoneMobile, checkIsPhoneMobile],
  )

  return (
    <SideDrawerForm
      onSave={onSave}
      primaryBtnLabel={contact ? 'Update Contact' : 'Create Contact'}
      disableSave={isSubmitting}
      onCancel={onCancel}
      containerTop={contact ? '73px' : '124px'}
    >
      <Form>
        <Grid sx={styles.formSection} container>
          <Grid item xs={12}>
            <Field
              name="firstName"
              label="First Name"
              component={MuiTextFieldFormik}
              type="string"
              fullWidth
              size="small"
              required={requireName}
              data-test-id="firstNameField"
            />
          </Grid>
          <Grid item xs={12}>
            <Field
              name="lastName"
              label="Last Name"
              component={MuiTextFieldFormik}
              type="string"
              fullWidth
              size="small"
              required={requireName}
              data-test-id="lastNameField"
            />
          </Grid>
          <Grid item xs={12}>
            <Field
              name="company"
              label="Company Name"
              component={MuiTextFieldFormik}
              type="string"
              fullWidth
              size="small"
              required={requireCompany}
              data-test-id="companyNameField"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sx={{ marginTop: (theme: Theme) => theme.spacing(2.4) }}
          >
            <HeaderAddButton disabled={addEmailDisabled} onClick={addEmail}>
              +Add additional email address
            </HeaderAddButton>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{ marginTop: (theme: Theme) => theme.spacing(1.2) }}
          >
            <EmailCollection
              name="emailAddresses"
              required={requireEmail}
              avoidValidateOnBlur
            />
          </Grid>
          <Grid container item spacing={2}>
            <Grid item xs={9}>
              <Field
                name="phoneNumber"
                label="Phone"
                component={MuiPhoneFieldFormik}
                type="string"
                required={requirePhone}
                fullWidth
                size="small"
                onBlur={onBlurPhone}
                helperText={isMobilePhone ? 'Number entered is Mobile' : ''}
                data-test-id="phoneField"
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="phoneExt"
                label="Ext"
                component={MuiTextFieldFormik}
                type="string"
                fullWidth
                size="small"
                disabled={!phoneNumber}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value
                  setFieldTouched('phoneExt', true)
                  setFieldValue('phoneExt', value)
                }}
                data-test-id="phoneExtField"
              />
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item xs={9}>
              <Field
                name="altPhoneNumber"
                label="Alternate Phone"
                component={MuiPhoneFieldFormik}
                type="string"
                fullWidth
                size="small"
                onBlur={onBlurPhone}
                helperText={isMobileAltPhone ? 'Number entered is Mobile' : ''}
                data-test-id="altPhoneField"
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="altPhoneExt"
                label="Ext"
                component={MuiTextFieldFormik}
                type="string"
                fullWidth
                size="small"
                disabled={!altPhoneNumber}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value
                  setFieldTouched('altPhoneExt', true)
                  setFieldValue('altPhoneExt', value)
                }}
                data-test-id="altPhoneExtField"
              />
            </Grid>
          </Grid>
          <Grid
            container
            item
            spacing={2}
            xs={12}
            sx={{ marginTop: (theme: Theme) => theme.spacing(2.4) }}
          >
            <AddressFields setFieldTouched={setFieldTouched} />
          </Grid>

          <Grid
            container
            item
            spacing={2}
            sx={{ marginTop: (theme: Theme) => theme.spacing(2.4) }}
          >
            <Grid item xs={12}>
              <Field
                name="contactMethod"
                label="Best Contact Method"
                component={BaseTextField}
                select
                data-test-id="bestContactMethodField"
              >
                <MenuItem value="" disabled>
                  Select Best Contact Method
                </MenuItem>
                <MenuItem value="Phone">Phone</MenuItem>
                <MenuItem value="Email">Email</MenuItem>
                <MenuItem value="SMS">SMS</MenuItem>
              </Field>
            </Grid>
            <Grid item xs={12}>
              <Field
                name="contactTime"
                label="Best Contact Time(s)"
                component={BaseTextField}
                select
                SelectProps={{
                  multiple: true,
                  value: contactTime,
                  onChange: handleChange,
                }}
                data-test-id="bestContactTimeField"
              >
                <MenuItem value="" disabled>
                  Select One or More Times
                </MenuItem>
                <MenuItem value="morning">Morning</MenuItem>
                <MenuItem value="afternoon">Afternoon</MenuItem>
                <MenuItem value="evening">Evening</MenuItem>
                <MenuItem value="weekend">Weekend</MenuItem>
              </Field>
            </Grid>
            {/* <Grid item xs={12}>
              <Field
                name="timeZone"
                label="Time Zone"
                component={MuiTextFieldFormik}
                disabled
                type="string"
                required
                fullWidth
                size="small"
              />
            </Grid> */}

            <Grid item xs={12}>
              <Field
                component={MuiCheckboxFormik}
                disabled={isPrimaryContact}
                label="Make Primary Contact"
                name="isPrimaryContact"
                size="small"
                color="primary"
                data-test-id="makePrimaryContactCheckbox"
              />
            </Grid>
          </Grid>
        </Grid>
      </Form>
    </SideDrawerForm>
  )
}
